import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  RefreshCw,
  Trash2,
  Loader2,
  Briefcase,
  FileJson,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { useJobList, useDeleteJobs } from "@/hooks/useJobs";
import { Toaster, toast } from "sonner";
import { cn } from "@/lib/utils";
import type { JobListItem, JobDeleteScope } from "@/services/jobsApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSessionState } from "@/hooks/useSessionState";
import { useUrlState } from "@/hooks/useUrlState";
import type { JobsSessionState, JobsUrlState } from "@/types/pageState";
import { JobResultModal } from "@/components/jobs/JobResultModal";

function StatusBadge({ status }: { status: string }) {
  const variant =
    status === "in_progress"
      ? "default"
      : status === "queued"
        ? "secondary"
        : status === "complete"
          ? "outline"
          : status === "failed"
            ? "destructive"
            : "outline";
  const label =
    status === "in_progress"
      ? "In progress"
      : status === "not_found"
        ? "Not found"
        : status;
  return (
    <Badge variant={variant} className={cn("uppercase text-xs")}>
      {label}
    </Badge>
  );
}

function formatDate(iso?: string) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

type SortKey = "job_id" | "function" | "status" | "enqueue_time";
type SortDirection = "asc" | "desc";

const DEFAULT_SORT_KEY: SortKey = "enqueue_time";
const DEFAULT_SORT_DIRECTION: SortDirection = "asc";

function JobsTable({
  jobs,
  sortKey,
  sortDirection,
  onSortChange,
  onResultClick,
}: {
  jobs: JobListItem[];
  sortKey: SortKey;
  sortDirection: SortDirection;
  onSortChange: (key: SortKey) => void;
  onResultClick?: (job: JobListItem) => void;
}) {
  const renderSortIcon = (columnKey: SortKey) => {
    const isActive = sortKey === columnKey;
    if (!isActive) {
      return <ArrowUpDown className="w-3 h-3 text-muted-foreground" />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="w-3 h-3" />;
    }
    return <ArrowDown className="w-3 h-3" />;
  };

  const handleHeaderClick = (columnKey: SortKey) => {
    onSortChange(columnKey);
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <button
              type="button"
              onClick={() => handleHeaderClick("job_id")}
              className="flex items-center gap-1 text-xs font-medium text-left"
            >
              <span>Job ID</span>
              {renderSortIcon("job_id")}
            </button>
          </TableHead>
          <TableHead>
            <button
              type="button"
              onClick={() => handleHeaderClick("function")}
              className="flex items-center gap-1 text-xs font-medium text-left"
            >
              <span>Function</span>
              {renderSortIcon("function")}
            </button>
          </TableHead>
          <TableHead>
            <button
              type="button"
              onClick={() => handleHeaderClick("status")}
              className="flex items-center gap-1 text-xs font-medium text-left"
            >
              <span>Status</span>
              {renderSortIcon("status")}
            </button>
          </TableHead>
          <TableHead className="text-right">
            <button
              type="button"
              onClick={() => handleHeaderClick("enqueue_time")}
              className="flex items-center gap-1 text-xs font-medium w-full justify-end"
            >
              <span>Enqueue time</span>
              {renderSortIcon("enqueue_time")}
            </button>
          </TableHead>
          {onResultClick && <TableHead className="w-[100px]"></TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {jobs.map((job) => (
          <TableRow key={job.job_id}>
            <TableCell className="font-mono text-xs">{job.job_id}</TableCell>
            <TableCell className="font-mono text-xs">
              {job.function ?? "—"}
            </TableCell>
            <TableCell>
              <StatusBadge status={job.status} />
            </TableCell>
            <TableCell className="text-right text-muted-foreground text-xs">
              {formatDate(job.enqueue_time ?? job.created_at)}
            </TableCell>
            {onResultClick && (
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onResultClick(job)}
                  className="gap-1.5"
                >
                  <FileJson className="w-3.5 h-3.5" />
                  Result
                </Button>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const DELETE_SCOPES: { value: JobDeleteScope; label: string }[] = [
  { value: "queued", label: "Queued only" },
  { value: "in_progress", label: "In progress only" },
  { value: "all", label: "All (queued & in progress)" },
];

export default function Jobs() {
  const { data, isLoading, refetch } = useJobList(5000);
  const deleteJobsMutation = useDeleteJobs();
  const [deleteScope, setDeleteScope] = useState<JobDeleteScope>("queued");

  const [urlState, setUrlState] = useUrlState<JobsUrlState>({
    schema: {
      sortKey: "string",
      sortDirection: "string",
    },
    defaults: {
      sortKey: DEFAULT_SORT_KEY,
      sortDirection: DEFAULT_SORT_DIRECTION,
    },
    replace: true,
  });

  // Session state for result modal
  const [sessionState, setSessionState] = useSessionState<JobsSessionState>({
    key: "jobs-ui",
    defaultValue: {
      resultModalOpen: false,
    },
  });

  const jobs = data?.jobs ?? [];
  const total = data?.total ?? 0;
  const activeJobs = jobs.filter(
    (j) => j.status === "queued" || j.status === "in_progress",
  );
  const queuedCount = jobs.filter((j) => j.status === "queued").length;
  const inProgressCount = jobs.filter((j) => j.status === "in_progress").length;
  const deleteScopeCount =
    deleteScope === "queued"
      ? queuedCount
      : deleteScope === "in_progress"
        ? inProgressCount
        : activeJobs.length;

  const currentSortKey: SortKey =
    (urlState.sortKey as SortKey | undefined) ?? DEFAULT_SORT_KEY;
  const currentSortDirection: SortDirection =
    (urlState.sortDirection as SortDirection | undefined) ??
    DEFAULT_SORT_DIRECTION;

  const sortJobs = (list: JobListItem[]): JobListItem[] => {
    const sorted = [...list].sort((a, b) => {
      const key = currentSortKey;
      let aVal: string | number | null | undefined;
      let bVal: string | number | null | undefined;

      if (key === "job_id") {
        aVal = a.job_id;
        bVal = b.job_id;
      } else if (key === "function") {
        aVal = a.function ?? "";
        bVal = b.function ?? "";
      } else if (key === "status") {
        aVal = a.status;
        bVal = b.status;
      } else {
        const aTime = a.enqueue_time ?? a.created_at ?? null;
        const bTime = b.enqueue_time ?? b.created_at ?? null;
        const aDate = aTime ? new Date(aTime).getTime() : 0;
        const bDate = bTime ? new Date(bTime).getTime() : 0;
        aVal = aDate;
        bVal = bDate;
      }

      if (aVal === bVal) return 0;
      if (aVal === undefined || aVal === null) return -1;
      if (bVal === undefined || bVal === null) return 1;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return aVal - bVal;
      }

      return String(aVal).localeCompare(String(bVal));
    });

    return currentSortDirection === "asc" ? sorted : sorted.reverse();
  };

  const sortedJobs = sortJobs(jobs);
  const sortedActiveJobs = sortJobs(activeJobs);

  const handleSortChange = (key: SortKey) => {
    setUrlState((prev) => {
      const nextDirection: SortDirection =
        (prev.sortKey as SortKey | undefined) === key &&
        prev.sortDirection === "asc"
          ? "desc"
          : "asc";

      return {
        ...prev,
        sortKey: key,
        sortDirection: nextDirection,
      };
    });
  };

  const handleResultClick = (job: JobListItem) => {
    setSessionState((prev) => ({
      ...prev,
      resultJobId: job.job_id,
      resultModalOpen: true,
    }));
  };

  const handleDeleteByScope = async () => {
    if (deleteScopeCount === 0) {
      const scopeLabel =
        DELETE_SCOPES.find((s) => s.value === deleteScope)?.label ??
        deleteScope;
      toast.info(`No ${scopeLabel.toLowerCase()} jobs to delete`);
      return;
    }
    const scopeLabel =
      DELETE_SCOPES.find((s) => s.value === deleteScope)?.label ?? deleteScope;
    if (
      !window.confirm(
        `Delete ${deleteScopeCount} job(s) (${scopeLabel})? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await deleteJobsMutation.mutateAsync(deleteScope);
      toast.success(`Jobs deleted (${scopeLabel})`);
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : `Failed to delete jobs (${scopeLabel})`,
      );
    }
  };

  return (
    <div className="space-y-6">
      <Toaster position="top-right" theme="dark" />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-xl font-semibold">Jobs</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh
          </Button>
          <Select
            value={deleteScope}
            onValueChange={(v) => setDeleteScope(v as JobDeleteScope)}
          >
            <SelectTrigger className="w-[220px] h-9">
              <SelectValue placeholder="Delete scope" />
            </SelectTrigger>
            <SelectContent>
              {DELETE_SCOPES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label} (
                  {s.value === "queued"
                    ? queuedCount
                    : s.value === "in_progress"
                      ? inProgressCount
                      : activeJobs.length}
                  )
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDeleteByScope}
            disabled={deleteScopeCount === 0 || deleteJobsMutation.isPending}
          >
            {deleteJobsMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            Delete ({deleteScopeCount})
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            Queued & in-progress jobs
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading jobs…
            </div>
          ) : activeJobs.length === 0 ? (
            <p className="text-muted-foreground py-8">
              No queued or in-progress jobs.
            </p>
          ) : (
            <JobsTable
              jobs={sortedActiveJobs}
              sortKey={currentSortKey}
              sortDirection={currentSortDirection}
              onSortChange={handleSortChange}
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            All Jobs
            <span className="text-muted-foreground font-normal text-sm">
              ({total} total)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading jobs…
            </div>
          ) : jobs.length === 0 ? (
            <p className="text-muted-foreground py-8">No jobs.</p>
          ) : (
            <JobsTable
              jobs={sortedJobs}
              sortKey={currentSortKey}
              sortDirection={currentSortDirection}
              onSortChange={handleSortChange}
              onResultClick={handleResultClick}
            />
          )}
        </CardContent>
      </Card>

      <JobResultModal
        open={sessionState.resultModalOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, resultModalOpen: open }))
        }
        jobId={sessionState.resultJobId}
      />
    </div>
  );
}
